<template>
  <OnboardingView v-if="isOnboarding()" />
  <UnsubscribeView v-else-if="isUnsubscribe()" />
  <template v-else>
    <ErrorComponent
      v-if="
        appStore.contentStatus && appStore.contentStatus !== 200 && appStore.contentStatus !== 404
      "
    />
    <div
      v-if="appStore.initialized === true"
      id="body-div"
      :class="{ 'overflow-auto': navigationStore.isSignInVisible === false }"
      class="h-full"
      @scroll="handleScroll()"
    >
      <template v-if="appStore.showAppSpinner === true">
        <div class="flex h-screen content-center items-center justify-center">
          <SpinnerComponent class="h-8 w-8" />
        </div>
      </template>
      <template v-else-if="navigationStore.isSignInVisible === true">
        <SplitScreenView>
          <RouterView />
        </SplitScreenView>
      </template>
      <template v-else>
        <!-- This div is only used on IOS, because when Iphone have notch, the status bar is transparent, this means content will be visible in the status bar -->
        <div
          v-if="Capacitor.getPlatform() === 'ios'"
          class="fixed top-0 z-20 h-16 w-full bg-gray-100"
        ></div>
        <NavigationComponent />
        <main id="main-div" class="py-4 lg:pl-72 2xl:py-10">
          <div
            class="container mx-auto px-4 sm:px-6 lg:px-8"
            :class="{ 'pointer-events-none': isScrolling === true }"
          >
            <BreadcrumbComponent class="mb-4 2xl:mb-8" />
            <RouterView />
          </div>
        </main>
      </template>
    </div>
    <div
      v-if="appStore.initialized === false"
      class="flex h-screen content-center items-center justify-center"
    >
      <SpinnerComponent class="h-8 w-8" />
    </div>
  </template>
</template>
<script setup lang="ts">
import { useAppStore } from '@/stores/portal-stores/general-stores/app'
import { useNavigationStore } from '@/stores/portal-stores/component-stores/navigation'
import { useIdLogonStore } from '@/stores/portal-stores/component-stores/IdLogon'
import SplitScreenView from '@/views/SplitScreenView.vue'
import { Capacitor } from '@capacitor/core'
import { App, type URLOpenListenerEvent } from '@capacitor/app'
import router from './router'
import { extractLanguage } from '@/application/helpers/index'
import type { INativeWrongLang } from '@/interfaces/helpers/ILanguage'
import type { QueryParameters } from './interfaces/IDeepLinkSettings'
import { Browser } from '@capacitor/browser'
import { lockPortraitOrientation } from '@/application/helpers'
import { debounce } from '@/application/helpers/utilities'
import { isOnboarding, isUnsubscribe } from '@/router/index'
import OnboardingView from '@/views/standalone/onboarding/OnboardingView.vue'
import UnsubscribeView from './views/standalone/UnsubscribeView.vue'
import { useNotificationCenterStore, usePushNotificationStore } from './stores'

const appStore = useAppStore()
const navigationStore = useNavigationStore()
const idLogonStore = useIdLogonStore()
const isScrolling = ref(false)

const debounceValueChange = debounce(() => (isScrolling.value = false), 250)

const handleScroll = () => {
  // If we are on native, we set isScrolling is true, so we can add 'pointer-events-none' to the div wrapping the RouterView.
  if (Capacitor.isNativePlatform() === true) {
    isScrolling.value = true
    debounceValueChange()
  }
}

const handleBeforeUnload = (event: BeforeUnloadEvent) => {
  if (appStore.canCloseApp === false) {
    event.preventDefault()
  }
}

Browser.addListener('browserFinished', () => {
  //If user cancels in app browser when using idlogin(mitId etc.) we need to cancenl the spinner on idLogin button, otherwise its continues to spin.
  console.log('In app browser closed by user')
  idLogonStore.setButtonSpinner(false)
})

window.addEventListener('beforeunload', handleBeforeUnload)

App.addListener('appStateChange', async ({ isActive }) => {
  console.log('App state changed. Is active?', isActive)

  // When app returns to foreground
  if (isActive && Capacitor.isNativePlatform()) {
    const pushNotificationStore = usePushNotificationStore()

    // Check if we have push notifications initialized
    if (!pushNotificationStore.initialized) {
      console.log('Push notifications not initialized, initializing now')
      // Initialize push notifications first
      await pushNotificationStore.initializePushNotifications()
    }
  }

  // notification polling
  const notificationStore = useNotificationCenterStore()
  if (isActive) {
    if (notificationStore.initialized) {
      console.log('Starting notification polling')
      notificationStore.startPolling()
    }
  } else {
    console.log('Stopping notification polling')
    notificationStore.stopPolling()
  }
})

App.addListener('appUrlOpen', async function (event: URLOpenListenerEvent) {
  const queryParameters: QueryParameters = {}

  const url = event.url
  const slugRegex = /(?:https?:\/\/[^/]+)?\/(.*)/

  const locale = localStorage.getItem('appLanguageLocale')
  const extractedLang = extractLanguage(url)

  appStore.setIsDeeplink(true)

  // Check if the locale is includes in the extracted locale, if not the url is different language from app
  if (locale != undefined && extractedLang?.locale.includes(locale) === false) {
    appStore.setNativeLinkIsAnotherLanguage({
      isWrongLang: true,
      locale: extractedLang?.locale,
      lang: extractedLang?.language?.fullName
    } as INativeWrongLang)
    console.log('url does not include chosenLanguageUrl', locale, extractedLang?.locale)
  }

  // Match the regex pattern
  const match = url.match(slugRegex)
  let slug = match ? match[1] : ''

  if (slug.includes('?')) {
    //Getting query parameters if we have one
    const queryParametersString = slug.split('?')[1]
    //Splitting query parameters by each paramter
    const pairs = queryParametersString.split('&')

    //Adding every paramter key and value to query parameter object and then pushing it through the router
    pairs.forEach((pair) => {
      const [key, value] = pair.split('=')
      queryParameters[key] = value
    })
  }

  console.log(
    'App opened with URL:',
    event,
    'url: ',
    url,
    'slug: ',
    slug,
    'query: ',
    queryParameters
  )

  if (!slug.startsWith('/')) {
    // If not, add a forward slash at the beginning
    slug = '/' + slug
  }

  if (navigationStore.isNavigating) {
    appStore.setDeepLinkSettings({ queryParameters: queryParameters, slug: slug })
  } else {
    // We only push to the route if there is a slug present
    if (slug) {
      if (Capacitor.isNativePlatform()) {
        // On iOS when redirecting from video chat(in app browser) back to the app, it wont close the browser.
        // By checking the videoMeeting query paramter we can force close the browser
        if (queryParameters.videoMeeting === 'true') {
          // If videoMeeting is not deleted, it just shows blank screen when redirect is done
          delete queryParameters.videoMeeting
          await Browser.close()
        } else if (
          queryParameters.transaction_id !== undefined &&
          queryParameters.transaction_id !== ''
        ) {
          //On ios when redirecting from idLogonRedirectView, it wont automatically close the in app browser, so we need to close the browser manually
          appStore.setAppSpinner(true)

          await Browser.close()
        }
      }

      router.push({ path: slug, query: queryParameters })
    }
  }
})

App.addListener('appRestoredResult', (data) => {
  console.log('Restored state:', data)
})

App.addListener('backButton', ({ canGoBack }) => {
  if (!canGoBack) {
    App.exitApp()
  } else {
    router.back()
    lockPortraitOrientation()
  }
})

onMounted(async () => {
  changeFaviconBasedOnBrand()

  if (Capacitor.isNativePlatform() === true) {
    lockPortraitOrientation()
  }

  window.addEventListener('resize', appStore.setInnerWidth)
})

onUnmounted(() => {
  window.removeEventListener('resize', appStore.setInnerWidth)
  window.removeEventListener('beforeunload', handleBeforeUnload)
})

const changeFaviconBasedOnBrand = () => {
  const iconUrl = `/favicons/favicon-${appStore.getBrand()}_candidate.ico`
  const link = document.querySelector('link[rel="icon"]') as HTMLAnchorElement

  if (link != undefined) {
    link.href = iconUrl
  } else {
    const newLink = document.createElement('link')
    newLink.rel = 'icon'
    newLink.href = iconUrl
    document.head.appendChild(newLink)
  }
}
</script>
